/**
 * 标准配合约束图求解器（Phase 2/3 收敛后的分发层）。
 *
 * 文件职责：
 * 1. residual-first：当前残差已满足且无主动分支切换时直接接受当前位姿；
 * 2. 求解分发：驱动场景内核先行（偏锚定保证驱动位姿分量按用户输入保持，投影可能给出
 *    "合法但撤销驱动意图"的解）；非驱动场景投影优先、失败后内核兜底。解析闭合规则库
 *    （Phase 2）与驱动平面回环求解器（Phase 3）均已退役到全局数值内核，
 *    其覆盖场景的钉子 fixture 永久保留在 __tests__/assembly；
 * 3. 保持输入输出为 cad-core plain data，不依赖 UI、Viewer 或 Object3D。
 */

import * as THREE from 'three';
import {
  AssemblyMateType,
  MateAlignment,
  MateConcentricMisalignmentType,
  MateSolveStatus,
  type AssemblyMateDefinition,
} from '../definition';
import {
  normalizeVector,
  resolveAxisGeometry,
} from './geometry/StandardMateGeometryAccess';
import {
  createComponentWorldDeltaMap,
  transformMateDefinitionForSolve,
  type ComponentWorldDeltaMap,
} from './geometry/StandardMateGeometryTransform';
import { resolveStandardMateLinearDimensionScale } from './dimension/StandardMateDimension';
import { validateSolvedMateDefinitions } from './validation/StandardMateConstraintValidator';
import { createSolvedDiagnostics } from './StandardMateCandidateResult';
import { trySolveStandardMateGlobalNumericKernel } from './StandardMateGlobalNumericSolver';
import {
  DEFAULT_STANDARD_MATE_LINEAR_TOLERANCE,
  resolveStandardMateConstraintTolerance,
  type StandardMateConstraintTolerance,
} from './StandardMateConstraintResidual';
import {
  appendStandardMateSolverDebugTraceEvent,
  type StandardMateSolverDebugTraceSession,
} from './StandardMateSolverDebugTrace';
import type {
  AssemblyMateSolverComponentState,
  StandardMateSolverInput,
  StandardMateSolverResult,
} from './StandardMateSolverTypes';

/**
 * 默认线性容差。
 *
 * 与主求解器、残差判定统一到单一容差源 {@link DEFAULT_STANDARD_MATE_LINEAR_TOLERANCE}，避免约束图
 * 在比主校验更松的容差下报 solved、把主校验视为越界的姿态写回，导致旋转数次后已提交配合被误判越界。
 * 导出以便测试守卫“默认容差不得再次分叉”。
 */
export const DEFAULT_GRAPH_SOLVER_EPSILON = DEFAULT_STANDARD_MATE_LINEAR_TOLERANCE;

/**
 * 读取 Mate 两端组件 ID。
 *
 * @param mate Mate Definition。
 * @returns 两端组件 ID。
 */
function resolveMateComponentIds(mate: AssemblyMateDefinition): readonly [string, string] {
  return [
    mate.firstEntity.componentInstanceId,
    mate.secondEntity.componentInstanceId,
  ] as const;
}

/**
 * 判断组件是否固定。
 *
 * @param component 组件状态。
 * @returns 是否固定。
 */
function isFixedComponent(component: AssemblyMateSolverComponentState | undefined): boolean {
  return component?.fixationState === 'fixed';
}

/**
 * 判断主动同轴心分支的有向轴向是否已经满足。
 *
 * 设计说明：
 * 圆柱孔/圆边的最终残差允许无向中心线，但用户主动切换 Aligned / AntiAligned 时，
 * 求解器必须按有向分支决定是否需要继续投影求解。
 *
 * @param mate 已按当前组件姿态变换后的 Mate。
 * @param tolerance 求解容差。
 * @returns 当前姿态是否已经满足主动分支。
 */
function isActiveConcentricAlignmentSatisfied(
  mate: AssemblyMateDefinition,
  tolerance: StandardMateConstraintTolerance,
): boolean {
  const firstAxis = resolveAxisGeometry(mate.firstEntity);
  const secondAxis = resolveAxisGeometry(mate.secondEntity);
  if (!firstAxis || !secondAxis) {
    return false;
  }
  const firstDirection = normalizeVector(firstAxis.direction, tolerance.linear);
  const secondDirection = normalizeVector(secondAxis.direction, tolerance.linear);
  if (!firstDirection || !secondDirection) {
    return false;
  }

  const expectedAngle = mate.alignment === MateAlignment.AntiAligned ? Math.PI : 0;
  return Math.abs(firstDirection.angleTo(secondDirection) - expectedAngle) <= tolerance.angular;
}

/**
 * 判断当前 residual-first 是否应让位给主动 Mate 求解。
 *
 * @param input 求解器输入。
 * @param tolerance 求解容差。
 * @returns 是否需要跳过 residual-first。
 */
function shouldBypassCurrentResidualSolveForActiveMates(
  input: StandardMateSolverInput,
  tolerance: StandardMateConstraintTolerance,
): boolean {
  const activeMateIds = new Set(input.activeMateIds ?? []);
  if (activeMateIds.size === 0) {
    return false;
  }

  const componentDeltaMap = createComponentWorldDeltaMap(input.components);
  return input.mateDefinitions.some((sourceMate) => {
    if (!activeMateIds.has(sourceMate.mateId) || sourceMate.suppressed) {
      return false;
    }
    const [firstComponentId, secondComponentId] = resolveMateComponentIds(sourceMate);
    if (
      isFixedComponent(input.components[firstComponentId])
      && isFixedComponent(input.components[secondComponentId])
    ) {
      return true;
    }
    if (sourceMate.mateType !== AssemblyMateType.Concentric) {
      return false;
    }

    const solvedMate = transformMateDefinitionForSolve(sourceMate, componentDeltaMap, input.components);
    return !isActiveConcentricAlignmentSatisfied(solvedMate, tolerance);
  });
}

/**
 * 克隆组件 transform 映射。
 *
 * @param components 组件状态。
 * @returns transform 映射。
 */
function cloneComponentTransforms(
  components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
): Record<string, AssemblyMateSolverComponentState['transform']> {
  const result: Record<string, AssemblyMateSolverComponentState['transform']> = {};
  Object.values(components).forEach((component) => {
    result[component.componentInstanceId] = {
      position: [...component.transform.position] as readonly [number, number, number],
      quaternion: [...component.transform.quaternion] as readonly [number, number, number, number],
      scale: [...component.transform.scale] as readonly [number, number, number],
    };
  });
  return result;
}

/**
 * 判断 Mate 是否包含需要主动重分配残差的求解目标。
 *
 * 设计说明：
 * Symmetric misaligned concentric 的校验语义是“偏差不超过最大值”，
 * 但求解语义还要求把 linked mate 与当前 mate 的偏差平均分摊。
 * 这类软目标即使当前 residual 校验通过，也不能被 residual-first 直接跳过。
 *
 * @param mate Mate Definition。
 * @returns 是否必须进入求解阶段。
 */
function requiresActiveResidualDistributionSolve(mate: AssemblyMateDefinition): boolean {
  return mate.mateType === AssemblyMateType.Concentric
    && mate.concentricMisalignment?.type === MateConcentricMisalignmentType.Symmetric;
}

/**
 * 创建当前输入姿态已经满足全部配合时的求解结果。
 *
 * @param input 原求解输入。
 * @param componentTransforms 当前组件 transform 映射。
 * @returns 标准求解结果。
 */
function createCurrentResidualSolvedResult(
  input: StandardMateSolverInput,
  componentTransforms: Readonly<Record<string, AssemblyMateSolverComponentState['transform']>>,
): StandardMateSolverResult {
  return {
    status: MateSolveStatus.Solved,
    componentTransforms,
    diagnostics: createSolvedDiagnostics(input.mateDefinitions),
  };
}

/**
 * 尝试直接接受当前输入姿态。
 *
 * 设计说明：
 * PropertyManager 预览与确认提交都会把“当前已摆好的组件姿态”作为 solver 输入。
 * 若整张约束图在 residual 容差内已经满足，继续执行逐条投影只会扰动已提交配合，
 * 因此这里先做全局 residual 校验，满足时直接返回当前 transform。
 *
 * @param input 求解器输入。
 * @param debugTrace 调试追踪会话。
 * @returns 当前姿态已满足时的结果；否则返回 null 继续后续求解。
 */
export function resolveStandardMateCurrentResidualSolve(
  input: StandardMateSolverInput,
  debugTrace?: StandardMateSolverDebugTraceSession | null,
): StandardMateSolverResult | null {
  const tolerance = resolveStandardMateConstraintTolerance({
    epsilon: input.epsilon,
    angleEpsilon: input.angleEpsilon,
  });
  if (input.mateDefinitions.some(requiresActiveResidualDistributionSolve)) {
    appendStandardMateSolverDebugTraceEvent(debugTrace, 'constraintGraph.residualFirst', {
      satisfied: false,
      diagnostics: [],
      reason: '当前 Mate 图包含需要主动分配残差的同轴心错位策略，不能直接接受输入姿态。',
    });
    return null;
  }

  if (shouldBypassCurrentResidualSolveForActiveMates(input, tolerance)) {
    appendStandardMateSolverDebugTraceEvent(debugTrace, 'constraintGraph.residualFirst', {
      satisfied: false,
      diagnostics: [],
      reason: '当前求解包含未满足有向分支的主动 Mate，不能用无向残差直接接受输入姿态。',
      activeMateIds: input.activeMateIds ?? [],
      tolerance,
    });
    return null;
  }

  const componentTransforms = cloneComponentTransforms(input.components);
  const componentDeltaMap = createComponentWorldDeltaMap(input.components);
  const validationDiagnostics = validateSolvedMateDefinitions({
    mateDefinitions: input.mateDefinitions,
    components: input.components,
    componentTransforms,
    componentDeltaMap,
    linearDimensionScale: resolveStandardMateLinearDimensionScale(input.linearDimensionScale),
    transientMateIds: input.transientMateIds,
    epsilon: tolerance.linear,
    angleEpsilon: tolerance.angular,
  });

  appendStandardMateSolverDebugTraceEvent(debugTrace, 'constraintGraph.residualFirst', {
    satisfied: validationDiagnostics.length === 0,
    diagnostics: validationDiagnostics,
    tolerance,
  });

  return validationDiagnostics.length === 0
    ? createCurrentResidualSolvedResult(input, componentTransforms)
    : null;
}

/**
 * 平面转动副求解坐标系。
 */
interface PlanarRevoluteSolveFrame {
  /** 转动副公共轴方向。 */
  readonly axisDirection: THREE.Vector3;
  /** 垂直于公共轴的第一投影基。 */
  readonly firstBasis: THREE.Vector3;
  /** 垂直于公共轴的第二投影基。 */
  readonly secondBasis: THREE.Vector3;
}

/**
 * 平面转动副求解变量。
 */
interface PlanarRevoluteSolveVariable {
  /** 被求解的组件 ID。 */
  readonly componentId: string;
  /** 参数数组中的起始下标。 */
  readonly parameterOffset: number;
  /** 是否允许求解器调整该组件在机构平面内的平移。 */
  readonly allowTranslation: boolean;
  /** 是否允许求解器调整该组件绕机构公共轴的旋转。 */
  readonly allowRotation: boolean;
}

/**
 * 平面转动副数值求解结果。
 */
interface PlanarRevoluteParameterSolveResult {
  /** 求解后的参数数组。 */
  readonly parameters: readonly number[];
  /** 最大二维关节残差。 */
  readonly maxResidual: number;
}

/**
 * 使用约束图自由度尝试修复逐条投影失败的标准配合求解结果。
 *
 * @param input 求解器输入。
 * @param projectedResult 逐条投影求解结果。
 * @returns 图求解结果；不适用时返回原结果。
 */
export function resolveStandardMateConstraintGraphSolve(
  input: StandardMateSolverInput,
  projectedResult: StandardMateSolverResult,
  debugTrace?: StandardMateSolverDebugTraceSession | null,
): StandardMateSolverResult {
  const hasDrivenComponents = (input.drivenComponentInstanceIds?.length ?? 0) > 0;

  // 驱动场景：内核先行、不看投影结果——逐条投影可能给出"合法但撤销驱动意图"的解
  // （把被驱动件转回基线即报 solved，正是历史上"转不动"的机制），偏锚定内核解才保证
  // 驱动位姿分量按用户输入保持。驱动平面回环求解器已于 Phase 3 退役，其覆盖场景
  // （四连杆/大装配子回环/组合A 空间机构）全部由内核以偏锚定变量单元求解，
  // 钉子 fixture 永久保留在 __tests__/assembly 作为回归防线。
  if (hasDrivenComponents) {
    const drivenKernelResult = trySolveStandardMateGlobalNumericKernel(input, debugTrace);
    if (drivenKernelResult) {
      return drivenKernelResult;
    }
  }

  if (projectedResult.status === MateSolveStatus.Solved) {
    appendStandardMateSolverDebugTraceEvent(debugTrace, 'constraintGraph.skip', {
      reason: '逐条投影结果已求解，约束图分支不介入。',
    });
    return projectedResult;
  }

  // 非驱动场景：全局数值内核作为逐条投影失败后的统一闭合兜底（validation 通过才返回非 null）。
  // 解析闭合规则库（绕铰链旋转 / 沿轴滑动）已于 Phase 2 退役，覆盖场景全部由内核求解。
  if (!hasDrivenComponents) {
    const numericKernelResult = trySolveStandardMateGlobalNumericKernel(input, debugTrace);
    if (numericKernelResult) {
      return numericKernelResult;
    }
  }

  return projectedResult;
}
